import { create } from 'zustand';
import { DocumentState, Document } from '../types';

interface DocumentStore extends DocumentState {
  addDocument: (document: Document) => void;
  removeDocument: (id: string) => void;
  updateDocument: (id: string, updates: Partial<Document>) => void;
  setUploadProgress: (id: string, progress: number) => void;
  toggleDocumentSelection: (id: string) => void;
  selectAllDocuments: () => void;
  clearSelection: () => void;
  setViewMode: (mode: 'grid' | 'list') => void;
  setSorting: (sortBy: 'name' | 'date' | 'size', order: 'asc' | 'desc') => void;
}

export const useDocumentStore = create<DocumentStore>((set, get) => ({
  documents: [],
  uploadProgress: {},
  selectedDocuments: [],
  viewMode: 'grid',
  sortBy: 'date',
  sortOrder: 'desc',

  addDocument: (document: Document) => {
    set(state => ({
      documents: [...state.documents, document]
    }));
  },

  removeDocument: (id: string) => {
    set(state => ({
      documents: state.documents.filter(doc => doc.id !== id),
      selectedDocuments: state.selectedDocuments.filter(docId => docId !== id)
    }));
  },

  updateDocument: (id: string, updates: Partial<Document>) => {
    set(state => ({
      documents: state.documents.map(doc => 
        doc.id === id ? { ...doc, ...updates } : doc
      )
    }));
  },

  setUploadProgress: (id: string, progress: number) => {
    set(state => ({
      uploadProgress: { ...state.uploadProgress, [id]: progress }
    }));
  },

  toggleDocumentSelection: (id: string) => {
    set(state => ({
      selectedDocuments: state.selectedDocuments.includes(id)
        ? state.selectedDocuments.filter(docId => docId !== id)
        : [...state.selectedDocuments, id]
    }));
  },

  selectAllDocuments: () => {
    const { documents } = get();
    set({ selectedDocuments: documents.map(doc => doc.id) });
  },

  clearSelection: () => {
    set({ selectedDocuments: [] });
  },

  setViewMode: (mode: 'grid' | 'list') => {
    set({ viewMode: mode });
  },

  setSorting: (sortBy: 'name' | 'date' | 'size', order: 'asc' | 'desc') => {
    set({ sortBy, sortOrder: order });
  },
}));